/**
 * ╔════════════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️  CRITICAL PWA FILE - DO NOT MODIFY WITHOUT CAREFUL CONSIDERATION  ⚠️   ║
 * ╠════════════════════════════════════════════════════════════════════════════╣
 * ║  This hook provides PWA installation functionality.                        ║
 * ║  Changes can break the app installation on Android/iOS.                    ║
 * ║                                                                            ║
 * ║  BEFORE MAKING ANY CHANGES:                                                ║
 * ║  1. Read /app/memory/PWA_PROTECTED.md                                      ║
 * ║  2. Test thoroughly on real Android devices                                ║
 * ║                                                                            ║
 * ║  Last verified working: April 7, 2026                                      ║
 * ╚════════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useCallback } from 'react';

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      // If no deferred prompt, show manual instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        alert('To install: Tap the Share button, then "Add to Home Screen"');
      } else if (isAndroid) {
        alert('To install: Tap the menu (⋮) button, then "Add to Home Screen" or "Install App"');
      } else {
        alert('To install: Look for the install option in your browser menu');
      }
      return { outcome: 'manual' };
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstallable(false);
      }
      setDeferredPrompt(null);
      return { outcome };
    } catch (error) {
      console.error('Install prompt error:', error);
      return { outcome: 'error', error };
    }
  }, [deferredPrompt]);

  return {
    isInstallable,
    isInstalled,
    promptInstall,
    canPrompt: !!deferredPrompt
  };
}
